import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  healthFacilities:[],
  selectedUser: null,
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  loading: false,
  error: null
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ page, limit,filter,filter_type,search }, { getState }) => {
    const { auth: { token } } = getState();
    const params = {
      page,
      limit,
      [`filter[${filter_type}]`]: `${filter}`,
      search

    }
    const response = await api.get(`${api_url_v1}/users`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);

// export const exportUsers = createAsyncThunk(
//   'users/exportUsers',
//   async ({ filter,filter_type }, { getState }) => {
//     const { auth: { token } } = getState();
//     const params = {
     
//       [`filter[${filter_type}]`]: `${filter}`,

//     }
//     const response = await api.get(`${api_url_v1}/users-export`, {
//       params,
//       headers: {
//         'Authorization': `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       }
//     });
    
//     return {
//       data: response.data.data
//     };
//   }
// );

export const exportUsers = createAsyncThunk(
  'users/exportUsers',
  async ({ filter, filter_type }, { getState }) => {
    const { auth: { token } } = getState();
    
    // Create params object with filter parameters
    const params = {};
    
    // Only add filter parameters if both arrays have values
    if (filter && filter_type) {
      const filterTypes = filter_type.split(',');
      const filterValues = filter.split(',');
      
      // Create filter parameters in the format required by the API
      filterTypes.forEach((type, index) => {
        if (filterValues[index]) {
          params[`filter[${type}]`] = filterValues[index];
        }
      });
    }
    
    const response = await api.get(`${api_url_v1}/users-export`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data
    };
  }
);



export const fetchUsersGeolocation = createAsyncThunk(
  'users/fetchUsersGeolocation',
  async (params, { getState }) => {
    const { auth: { token } } = getState();

    // Convert array values to strings
    const processedParams = {};
    for (const key in params) {
      processedParams[key] = Array.isArray(params[key]) 
        ? params[key].join(',') // Convert array to comma-separated string
        : params[key];
    }
    
    const response = await api.get(`${api_url_v1}/users-geolocation`, {
      params: processedParams,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return response.data;
  }
);
export const fetchHealthFacilitiesGeolocation = createAsyncThunk(
  'users/fetchHealthFacilitesGeolocation',
  async (_,{ getState }) => {
    const { auth: { token } } = getState();
    
    const response = await api.get(`${api_url_v1}/health-facilities-geolocation`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return response.data;
  }
);


export const fetchUsersInRadius = createAsyncThunk(
  'users/fetchUsersInRadius',
  async ({params,latitude,longitude,radius}, { getState }) => {
    const { auth: { token } } = getState();

    const processedParams = {};
    for (const key in params) {
      processedParams[key] = Array.isArray(params[key]) 
        ? params[key].join(',') // Convert array to comma-separated string
        : params[key];
    }
    const response = await api.get(`${api_url_v1}/users-within-radius?latitude=${latitude}&longitude=${longitude}&radius=${radius}`, {
      params:processedParams,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);
export const fetchUserDetails = createAsyncThunk(
  'users/fetchUserDetails',
  async (slug, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/users/${slug}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);


export const updateUserStatus = createAsyncThunk(
  'users/updateUserStatus',
  async ({ userId, status }, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.put(`${api_url_v1}/users/${userId}`, { status }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return { userId, status }; 
  }
);

const userSlice = createSlice({
  name: 'usersSlice',
  initialState,
  reducers: {
   setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(exportUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportUsers.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(exportUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUsersGeolocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsersGeolocation.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
      })
      .addCase(fetchUsersGeolocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchHealthFacilitiesGeolocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthFacilitiesGeolocation.fulfilled, (state, action) => {
        state.loading = false;
        state.healthFacilities = action.payload.data;
      })
      .addCase(fetchHealthFacilitiesGeolocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.data;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUsersInRadius.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsersInRadius.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
      })
      .addCase(fetchUsersInRadius.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateUserStatus.pending, (state, action) => {
        // state.loading=true
       })
       .addCase(updateUserStatus.fulfilled, (state, action) => {
        const { userId, status } = action.payload;
        const user = state.items.find(user => user.id === userId);
        if (user) {
          user.status = status;
        }
        if (state.selectedUser?.id === userId) {
          state.selectedUser.status = status;
        }

          state.loading = false;
      });

    
     
  }
});

export const {setSelectedUser } = userSlice.actions;
export default userSlice.reducer;
